import React, { useMemo, useState } from 'react';
import './V1MarketPlusRsPanel.css';

function fmtRs(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toFixed(2)}`;
}

function fmtMove(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '+' : '';
  return `${sign}₹${v.toFixed(2)}`;
}

/**
 * Market-wide sticky list: names that reached Master +₹N/sh from prev close today.
 * Mounted below the CNC delivery pipeline on Normal Trading V1.
 */
export default function V1MarketPlusRsPanel({ rows = [], meta = null }) {
  const [open, setOpen] = useState(true);
  const threshold = Number(meta?.thresholdRs) > 0
    ? Number(meta.thresholdRs)
    : (Number(rows?.[0]?.thresholdRs) > 0 ? Number(rows[0].thresholdRs) : 3);
  const list = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort(
      (a, b) =>
        (Number(b.peakMoveRs || b.moveRs) - Number(a.peakMoveRs || a.moveRs))
        || (Number(b.ltpMoveRs) - Number(a.ltpMoveRs))
    );
  }, [rows]);
  const count = meta?.count != null ? meta.count : list.length;
  const scanned = meta?.quotesScanned;
  const universe = meta?.universeSize;

  return (
    <section className="v1-plus-rs card" aria-label={`Market +₹${threshold}/sh today`}>
      <header className="v1-plus-rs-head">
        <button
          type="button"
          className="v1-plus-rs-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <h3>
            +₹{threshold.toFixed(threshold % 1 ? 2 : 0)}/SH TODAY
            <span className="v1-plus-rs-badge">WHOLE MARKET</span>
          </h3>
          <span className="v1-plus-rs-meta">
            {count} hit{count === 1 ? '' : 's'}
            {universe != null ? ` · scan ${universe}` : ''}
            {scanned != null ? ` · quoted ${scanned}` : ''}
            <span className="v1-plus-rs-chevron" aria-hidden>{open ? '▾' : '▸'}</span>
          </span>
        </button>
        <p className="v1-plus-rs-sub">
          Names whose day high (or LTP) reached +₹{threshold % 1 ? threshold.toFixed(2) : threshold}/share
          from yesterday&apos;s close — sticky for the day, not just bot slots.
        </p>
      </header>
      {open && (
        list.length === 0 ? (
          <p className="v1-plus-rs-empty">
            No market names at +₹{threshold % 1 ? threshold.toFixed(2) : threshold}/sh yet.
            Scanning NSE/BSE liquid universe in the background…
          </p>
        ) : (
          <div className="v1-plus-rs-table-wrap">
            <table className="v1-plus-rs-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Stock</th>
                  <th>Exch</th>
                  <th>LTP</th>
                  <th>Day high</th>
                  <th>Peak +₹/sh</th>
                  <th>LTP +₹/sh</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row) => (
                  <tr key={row.symbol || row.stock}>
                    <td>{row.rank ?? '—'}</td>
                    <td className="v1-plus-rs-stock">{row.stock || row.symbol}</td>
                    <td>{row.exchange || '—'}</td>
                    <td>{fmtRs(row.price)}</td>
                    <td>{fmtRs(row.dayHigh)}</td>
                    <td className="up">{fmtMove(row.peakMoveRs ?? row.moveRs)}</td>
                    <td className={(Number(row.ltpMoveRs) || 0) >= 0 ? 'up' : 'down'}>
                      {fmtMove(row.ltpMoveRs)}
                    </td>
                    <td className={(Number(row.changePct) || 0) >= 0 ? 'up' : 'down'}>
                      {Number.isFinite(Number(row.changePct))
                        ? `${Number(row.changePct) >= 0 ? '+' : ''}${Number(row.changePct).toFixed(2)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}
